import * as React from "react";

import type { WifiNetwork } from "@volley/shared/coach-models";

import logger from "../../log";
import fetchApi, { FetchError } from "../../util/fetchApi";
import { countHiddenNetworks, toSsidOptions } from "../../util/wifiNetworks";

/**
 * How long to allow for a scan. The trainer gives motion 8s and the server
 * waits 15s, so the browser has to outlast both — fetchApi's 10s default would
 * abort a scan that was about to return.
 */
const SCAN_TIMEOUT_MS = 20_000;

export interface WifiScan {
    /** SSIDs the trainer can see, strongest first. Empty until a scan runs. */
    ssidOptions: string[];
    scanning: boolean;
    /** Result or failure text, safe to show as a field's helper text. */
    scanMessage: string;
    scan: (trainerId: number) => Promise<void>;
}

/**
 * Ask a trainer which wifi networks it can see.
 *
 * Shared by the paired-trainer dialog and the admin network panel, so the
 * failure wording stays identical in both. No failure here blocks setting
 * credentials by hand: a scan is an aid to typing the SSID, not a gate on it.
 */
export function useWifiScan(): WifiScan {
    const [ssidOptions, setSsidOptions] = React.useState<string[]>([]);
    const [scanning, setScanning] = React.useState(false);
    const [scanMessage, setScanMessage] = React.useState("");

    const scan = React.useCallback(async (trainerId: number) => {
        setScanning(true);
        setScanMessage("");
        try {
            const { networks } = await fetchApi<{ networks: WifiNetwork[] }>(
                `/api/trainers/${trainerId}/wifi-networks`,
                "POST",
                undefined,
                { signal: AbortSignal.timeout(SCAN_TIMEOUT_MS) },
            );

            const options = toSsidOptions(networks);
            setSsidOptions(options);

            if (options.length === 0) {
                setScanMessage(
                    "The trainer saw no named networks. Check that it is in range.",
                );
                return;
            }

            const hidden = countHiddenNetworks(networks);
            setScanMessage(
                `Found ${options.length} ${options.length === 1 ? "network" : "networks"}` +
                    (hidden > 0 ? ` (${hidden} hidden)` : ""),
            );
        } catch (err: unknown) {
            logger.error("Error scanning wifi networks", { error: err });

            const status = err instanceof FetchError ? err.statusCode : 0;
            if (status === 409) {
                // Expected whenever the trainer is powered off. Credentials can
                // still be saved — they apply at its next boot — so this is a
                // note, not a failure of what the user came here to do.
                setScanMessage(
                    "The trainer is offline, so it cannot scan. You can still enter the network name by hand.",
                );
            } else if (status === 504) {
                // The trainer is connected, the scan just did not finish. Do
                // not imply it is unreachable.
                setScanMessage(
                    "The trainer did not finish scanning in time. Try again, or enter the network name by hand.",
                );
            } else if (status === 501) {
                setScanMessage(
                    "This trainer's software is too old to scan for networks.",
                );
            } else {
                setScanMessage(
                    "Could not scan for networks. Enter the network name by hand.",
                );
            }
        } finally {
            setScanning(false);
        }
    }, []);

    return { ssidOptions, scanning, scanMessage, scan };
}

export default useWifiScan;
